import { Component, DestroyRef, computed, inject, input, linkedSignal, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router, RouterLink } from '@angular/router';
import { finalize, Observable } from 'rxjs';
import { BreadcrumbComponent } from '../../../shared/components/breadcrumb/breadcrumb.component';
import { InputBuscarComponent } from '../../../shared/components/input-buscar/input-buscar.component';
import { LucideModule } from '../../../shared/modules/lucide.module';
import { AlertaService } from '../../../shared/utils/alerta.service';
import { slugDeServico } from '../../../shared/utils/slug.util';
import { TodosServicosItem } from '../models/todos-servicos.model';
import { LinksDinamicosComponent } from '../links-dinamicos/links-dinamicos.component';
import { CatalogoPublicoService } from '../services/catalogo-publico.service';
import { FavoritosService } from '../services/favoritos.service';
import { TodosServicosUsuarioLogadoComponent } from './todos-servicos-usuario-logado/todos-servicos-usuario-logado.component';

const ACENTOS = /\p{Diacritic}/gu;

const ORDEM_ALFABETICA = new Intl.Collator('pt-BR', { sensitivity: 'base', numeric: true });

function normalizar(valor: string): string {
  return valor.normalize('NFD').replace(ACENTOS, '').toLowerCase().trim();
}

const ERRO_FAVORITO = 'Não foi possível atualizar os seus favoritos agora. Tente novamente em instantes.';

/**
 * A categoria que o parâmetro `/servicos/:categoria` nomeia, ou `undefined`.
 *
 * Primeiro pelo **slug do nome**, que é o link que "Categorias de Serviços" gera (decisão de
 * 29/09/2026); depois pelo **código**, aceito só por compatibilidade com links antigos —
 * nenhuma tela gera mais link de categoria por código. O nome não é único no adm: entre
 * homônimas, vale a primeira da lista, que já vem na ordem editorial.
 *
 * Exportada e pura para ser testada sem criar o componente.
 */
export function categoriaDaRota(
  categorias: readonly { id: string; titulo: string }[],
  parametro: string
): string | undefined {
  if (!parametro) return undefined;

  const peloSlug = categorias.find((categoria) => slugDeServico(categoria.titulo) === parametro);

  return peloSlug?.id ?? categorias.find((categoria) => categoria.id === parametro)?.id;
}

/**
 * A tela "Todos os Serviços": o menu de categorias à esquerda e os serviços da categoria
 * escolhida à direita.
 *
 * Todo o conteúdo vem do catálogo público da API — não há lista estática aqui. A busca
 * filtra no cliente, sobre a lista inteira, porque o contrato do back-end não tem
 * parâmetro de busca nem de paginação.
 */
@Component({
  selector: 'app-todos-servicos',
  templateUrl: './todos-servicos.component.html',
  styleUrls: ['./todos-servicos.component.css'],
  imports: [
    LinksDinamicosComponent,
    TodosServicosUsuarioLogadoComponent,
    InputBuscarComponent,
    RouterLink,
    LucideModule,
    BreadcrumbComponent,
  ],
})
export class TodosServicosComponent {
  private readonly catalogo = inject(CatalogoPublicoService);
  private readonly favoritosService = inject(FavoritosService);
  private readonly alerta = inject(AlertaService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);

  /** Os serviços cuja estrela está gravando: fica desabilitada até a resposta. */
  private readonly gravando = signal<ReadonlySet<string>>(new Set());

  readonly categorias = this.catalogo.categorias;
  readonly carregando = this.catalogo.carregando;
  readonly falhou = this.catalogo.falhou;

  /**
   * O parâmetro da rota `/servicos/:categoria`: o slug do nome da categoria, ou o
   * `codCategoriaServicoCtg` de um link antigo (ver `categoriaDaRota`).
   */
  readonly categoria = input('');

  /**
   * A categoria aberta. Segue a rota quando ela nomeia uma categoria que existe;
   * senão, cai na primeira com serviço — e só depois na primeira do menu, para que uma
   * categoria vazia não seja a primeira coisa que o cidadão vê.
   */
  readonly categoriaSelecionada = linkedSignal<readonly { id: string; titulo: string }[], string>({
    source: () => this.categorias(),
    computation: (categorias, anterior) => {
      const existe = (id: string) => categorias.some((categoria) => categoria.id === id);

      const daRota = categoriaDaRota(categorias, this.categoria());

      if (daRota) return daRota;
      if (anterior && existe(anterior.value)) return anterior.value;

      const comServico = this.categorias().find((categoria) => categoria.servicos.length);

      return comServico?.id ?? categorias[0]?.id ?? '';
    },
  });

  readonly termoBusca = signal('');

  readonly fechados = linkedSignal<unknown, ReadonlySet<string>>({
    source: () => ({ categoria: this.categoriaSelecionada(), termo: this.termoBusca() }),
    computation: () => new Set<string>(),
  });

  readonly categoriaAtiva = computed(() =>
    this.categorias().find((categoria) => categoria.id === this.categoriaSelecionada())
  );

  readonly servicos = computed<readonly TodosServicosItem[]>(() => {
    const termo = normalizar(this.termoBusca());
    const servicos = this.categoriaAtiva()?.servicos ?? [];

    const filtrados = termo
      ? servicos.filter((servico) =>
          normalizar(`${servico.titulo} ${servico.descricao}`).includes(termo)
        )
      : servicos;

    return [...filtrados].sort((a, b) => ORDEM_ALFABETICA.compare(a.titulo, b.titulo));
  });

  /** Catálogo vazio não é falha: é órgão sem serviço publicado, ou host não mapeado. */
  readonly vazio = computed(
    () => !this.carregando() && !this.falhou() && this.categorias().length === 0
  );

  constructor() {
    // As estrelas leem os favoritos da conta. Sem sessão o serviço não chama a API.
    this.favoritosService.carregar();
  }

  selecionarCategoria(id: string): void {
    this.categoriaSelecionada.set(id);
  }

  recarregar(): void {
    this.catalogo.recarregar();
  }

  aoBuscar(evento: Event): void {
    this.termoBusca.set((evento.target as HTMLInputElement).value);
  }

  limparBusca(): void {
    this.termoBusca.set('');
  }

  alternarServico(id: string): void {
    this.fechados.update((atual) => {
      const proximo = new Set(atual);
      if (!proximo.delete(id)) {
        proximo.add(id);
      }
      return proximo;
    });
  }

  estaAberto(id: string): boolean {
    return !this.fechados().has(id);
  }

  /** O `id` do item é o `codServicoSrv` em texto; o favorito é guardado pelo código. */
  ehFavorito(id: string): boolean {
    return this.favoritosService.ehFavorito(Number(id));
  }

  estaGravando(id: string): boolean {
    return this.gravando().has(id);
  }

  dicaFavorito(servico: TodosServicosItem): string {
    return this.ehFavorito(servico.id) ? 'Desfavoritar' : 'Favoritar';
  }

  rotuloFavorito(servico: TodosServicosItem): string {
    return this.ehFavorito(servico.id)
      ? `Desfavoritar ${servico.titulo}`
      : `Favoritar ${servico.titulo}`;
  }

  /**
   * Favoritar e desfavoritar passam por confirmação e gravam na conta do cidadão: a estrela só
   * muda depois da resposta. Sem sessão, a estrela pede login — o favorito não fica no
   * navegador.
   */
  async alternarFavorito(servico: TodosServicosItem): Promise<void> {
    if (this.estaGravando(servico.id)) return;

    if (!this.favoritosService.logado()) {
      await this.pedirLogin(servico);
      return;
    }

    const favoritado = this.ehFavorito(servico.id);

    const confirmado = await this.alerta.confirmar({
      titulo: favoritado ? 'Remover dos favoritos?' : 'Adicionar aos favoritos?',
      texto: favoritado
        ? `${servico.titulo} deixa de aparecer nos seus serviços favoritos.`
        : `${servico.titulo} passa a aparecer nos seus serviços favoritos.`,
      confirmar: favoritado ? 'Sim, remover' : 'Sim, adicionar',
      cancelar: 'Cancelar',
      tom: favoritado ? 'perigo' : 'info',
    });

    if (!confirmado) return;

    const codigo = Number(servico.id);

    // `unknown`: as duas gravações emitem tipos diferentes, e numa união de `Observable` o
    // TypeScript não resolve as sobrecargas de `pipe` e `subscribe`. A tela só espera o fim.
    const gravacao: Observable<unknown> = favoritado
      ? this.favoritosService.desfavoritar(codigo)
      : this.favoritosService.favoritar(codigo);

    this.marcarGravando(servico.id, true);

    gravacao
      .pipe(
        finalize(() => this.marcarGravando(servico.id, false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        error: () => void this.alerta.erro({ titulo: 'Favoritos', texto: ERRO_FAVORITO }),
      });
  }

  /** "Entrar" volta a esta mesma tela depois do login, pelo `?destino=` de `/login`. */
  private async pedirLogin(servico: TodosServicosItem): Promise<void> {
    const entrar = await this.alerta.confirmar({
      titulo: 'Entre para favoritar',
      texto: `Os favoritos ficam guardados na sua conta. Entre para adicionar ${servico.titulo} aos seus serviços favoritos.`,
      confirmar: 'Entrar',
      cancelar: 'Agora não',
      tom: 'info',
    });

    if (entrar) {
      void this.router.navigate(['/login'], { queryParams: { destino: this.router.url } });
    }
  }

  private marcarGravando(id: string, gravando: boolean): void {
    this.gravando.update((atual) => {
      const proximo = new Set(atual);

      if (gravando) {
        proximo.add(id);
      } else {
        proximo.delete(id);
      }

      return proximo;
    });
  }
}
